import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { servicesData } from '../../data/servicesData';

const ServicesListPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const seo = {
    metaTitle: "Our Software & AI Services | Byte Pairs Dharapuram",
    metaDescription: "Explore our range of custom software development, web applications, mobile apps, WhatsApp API automation, and SEO services in Dharapuram, Tamil Nadu.",
    keywords: "custom software, web application development, mobile apps, AI automation, WhatsApp API, SEO optimization, Dharapuram IT company",
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100 } },
  };

  return (
    <>
      <Helmet>
        <title>{seo.metaTitle}</title>
        <meta name="description" content={seo.metaDescription} />
        <meta name="keywords" content={seo.keywords} />
        <link rel="canonical" href="https://bytepairs.com/services" />
        <meta property="og:title" content={seo.metaTitle} />
        <meta property="og:description" content={seo.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://bytepairs.com/services" />

        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://bytepairs.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Services",
                "item": "https://bytepairs.com/services"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background text-foreground">
        <Header />

        <main className="pt-20">
          {/* Hero Section */}
          <section className="relative py-24 bg-gradient-to-br from-primary/5 via-muted/30 to-accent/5 overflow-hidden border-b border-border">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[linear-gradient(rgba(234,179,8,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(234,179,8,0.1)_1px,transparent_1px)] bg-[size:40px_40px]"></div>
            </div>
            <div className="max-w-6xl mx-auto px-6 relative z-10 text-center">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <Icon name="Layers" size={16} />
                  <span>Expert Solutions</span>
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="text-4xl md:text-6xl font-black mb-6 tracking-tight"
              >
                Our <span className="text-gradient">Services</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed"
              >
                We build high-performance custom applications, web portals, mobile apps, and lightweight automations that streamline operations and increase revenue.
              </motion.p>
            </div>
          </section>

          {/* Services Grid */}
          <section className="py-20 bg-background">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Explore Our <span className="text-gradient">Services</span>
                </h2>
                <p className="text-text-secondary max-w-2xl mx-auto">
                  From custom billing systems to AI-powered automation — choose the solution that fits your business needs.
                </p>
              </div>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="show"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {Object.values(servicesData).map((srv) => (
                  <motion.div
                    key={srv.id}
                    variants={itemVariants}
                    className="card flex flex-col justify-between p-8 hover:-translate-y-2 transition-all duration-300 relative overflow-hidden group"
                  >
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-primary/10 to-accent/10 rounded-bl-[80px] -z-10 group-hover:scale-125 transition-transform duration-500"></div>
                    <div>
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-6 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                        <Icon name={srv.icon} size={24} />
                      </div>
                      <h3 className="text-2xl font-bold mb-3 text-foreground group-hover:text-primary transition-colors duration-300">
                        {srv.title}
                      </h3>
                      <p className="text-text-secondary text-sm leading-relaxed mb-6">
                        {srv.tagline}
                      </p>
                    </div>

                    <div className="space-y-4">
                      {/* Short list of what's included */}
                      <div className="border-t border-border/60 pt-4 space-y-2">
                        {srv.features.slice(0, 2).map((feat, idx) => (
                          <div key={idx} className="flex items-start space-x-2 text-xs text-text-secondary">
                            <Icon name="CheckCircle2" size={14} className="text-primary mt-0.5 flex-shrink-0" />
                            <span>{feat.split(':')[0]}</span>
                          </div>
                        ))}
                      </div>

                      <Button
                        variant="outline"
                        fullWidth
                        iconName="ArrowRight"
                        iconPosition="right"
                        className="border-primary/20 text-primary hover:bg-primary/5 hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-all duration-300 font-semibold"
                        onClick={() => navigate(`/services/${srv.id}`)}
                      >
                        Explore Service
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* CTA Footer Section */}
          <section className="py-20 bg-gradient-to-br from-background via-muted/30 to-background border-t border-border">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Need a Custom Solution?
              </h2>
              <p className="text-text-secondary mb-8 max-w-2xl mx-auto">
                Not sure which service fits your project? Use our interactive project estimator or schedule a direct developer call.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button
                  variant="default"
                  iconName="PhoneCall"
                  iconPosition="left"
                  className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-brand"
                  onClick={() => navigate('/contact-engagement-hub')}
                >
                  Book Consultation
                </Button>
                <Button
                  variant="outline"
                  iconName="Calculator"
                  iconPosition="left"
                  className="border-primary/20 text-primary hover:bg-primary/5"
                  onClick={() => navigate('/contact-engagement-hub?tab=estimator')}
                >
                  Estimate Project Cost
                </Button>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default ServicesListPage;
